/**
 * Toast Utility Functions - Minimal Implementation
 * 
 * Only essential utility functions that cannot be replaced by showSuccess/showError.
 * All other functions have been removed - use showSuccess() and showError() directly.
 * 
 * @module ToastUtils
 */

import toast from 'react-hot-toast';
import { toastService } from './ToastService';
import { toastManager } from './ToastManager';

// Note: ToastUtils intentionally imports both toastService and toastManager
// - toastService: Used for showLoadingToast (needs deduplication)
// - toastManager: Used for dismissAllToasts (needs direct access for cleanup)
// This is acceptable as they are internal dependencies within the toast module

// ==================== TOAST MANAGEMENT FUNCTIONS ====================

/**
 * Dismiss all toasts
 * Ensures cleanup of registered messages even if onDismiss doesn't fire for all toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
  toastManager.clearAll();
};

/**
 * Dismiss specific toast
 * @param {string} toastId - The toast ID to dismiss
 */
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};

/**
 * Show loading toast with automatic deduplication
 * Loading toasts are persistent and require manual dismissal via dismissToast()
 * 
 * @param {string} message - The message to display
 * @param {object} options - Optional toast options (duration will be overridden to Infinity)
 * @returns {string|null} Toast ID for dismissal, or null if duplicate
 * 
 * @example
 * const toastId = showLoadingToast('Uploading file...');
 * // ... do work ...
 * dismissToast(toastId); // Dismiss when done
 */
export const showLoadingToast = (message, options = {}) => {
  return toastService.loading(message, options);
};
